import { getFormValues } from 'redux-form';

import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';

import http from '../../../../shared/http';
import checkIsExcludedCountryCode from '../../../../shared/checkIsExcludedCountryCode';
import endpoints from '../../../../../config/endpoints';
import { createSelectOptionsFromSysEnums } from '../../../../shared/form-elements/select';
import { actions as parentActions } from '../../../editor';

const defaultInitialState = {
	currentLanguage: 'en',
	translateFields: []
};

const createRestrictionGeoForm = ({
	namespace,
	stateNodeName
}) => {
	const initialState = {
		...defaultInitialState
	};

	// action types
	const FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-form-submit-success`;
	const FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-form-submit-error`;

	const FETCH_LOCATION_LOOKUPS_SUCCESS = `${namespace}/${stateNodeName}-fetch-location-lookups-success`;
	const FETCH_LOCATION_LOOKUPS_ERROR = `${namespace}/${stateNodeName}-fetch-location-lookups-error`;

	const DELETE_REQUEST_SUCCESS = `${namespace}/${stateNodeName}-delete-request-success`;
	const DELETE_REQUEST_ERROR = `${namespace}/${stateNodeName}-delete-request-errror`;

	const CHANGE_LANGUAGE = `${namespace}/${stateNodeName}-change-language`;

	const TRANSLATION_REQUEST_SUCCESS = `${namespace}/${stateNodeName}-translation-request-success`;
	const TRANSLATION_REQUEST_ERROR = `${namespace}/${stateNodeName}-translation-request-error`;

	// actions
	const actions = {
		restrictedGeoSubmit: ({ geo }) => (dispatch, getState) => {
			const {  offer } = getState().programsEditor;

			const prepareData = geo.map(entity => {
				const locationId = entity.location && entity.location[0]['value'];
				const data =  {
					location_id: locationId, text: entity.text.replace('<p>', '').replace('</p>', '')
				};
				if (entity.id) {
					data['id'] = entity.id;
				}

				return data;
			});

			const request = http({
				url: endpoints.OFFERS.LOCATION_RESTRICTIONS.replace('{id}', offer.id),
				method: 'put',
				actions: [
					FORM_SUBMIT_SUCCESS,
					FORM_SUBMIT_ERROR
				],
				data: prepareData
			});

			dispatch(parentActions.toggleProgressBar());

			return request(dispatch).then(action => {
				if (action.type === FORM_SUBMIT_SUCCESS) {
					dispatch(parentActions.fetchLocationRestrictions(offer.id));
					dispatch(parentActions.toggleProgressBar());
				}
			});

		},
		restrictedGeoTranslateSubmit: ({ geo }) => (dispatch, getState) => {
			const { offer, restrictionGeoForm: { currentLanguage } } = getState().programsEditor;

			const prepareData = geo.map(entity => ({
				location_restriction_id: entity.id,
				text: entity.text
			}));

			const request = actions.translationRequest(offer.id, currentLanguage, 'put', prepareData);
			return dispatch(request).then(action => {
				if (action.type === TRANSLATION_REQUEST_SUCCESS) {
					dispatch(actions.restrictedGeoChangeLangs(currentLanguage));
				}
			});
		},
		restrictedGeolocationsLookups: text => (dispatch, getState) => {
			const { geo } = getFormValues('offerRestrictionGeoForm')(getState());

			const selectedLocations =
				geo
					.filter(entity => entity.location)
					.map(entity => entity.location[0]['value']);

			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS}?query=${text}`,
				method: 'get',
				actions: [FETCH_LOCATION_LOOKUPS_SUCCESS, FETCH_LOCATION_LOOKUPS_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_LOCATION_LOOKUPS_SUCCESS) {
					const { data } = action.payload;
					const filteredData = data
						.filter(entity => selectedLocations.indexOf(entity.id) === -1)
						.filter(entity => !checkIsExcludedCountryCode(entity.country_code));

					return {
						options: createSelectOptionsFromSysEnums(filteredData)
					};
				}
			});
		},

		restrictedGeoDelete: id => (dispatch, getState) => {
			const { offer } = getState().programsEditor;

			const request = http({
				url: endpoints.OFFERS.LOCATION_RESTRICTIONS_DELETE.replace('{id}', id),
				method: 'delete',
				actions: [
					DELETE_REQUEST_SUCCESS,
					DELETE_REQUEST_ERROR
				]
			});

			request(dispatch).then(action => {
				if (action.type === DELETE_REQUEST_SUCCESS) {
					dispatch(parentActions.toggleProgressBar());
					dispatch(parentActions.fetchLocationRestrictions(offer.id));
				}
			});
		},

		restrictedGeoChangeLangs: lang => (dispatch, getState) => {
			const { defaultLanguage, offer } = getState().programsEditor;
			const actionChangeLang = {type: CHANGE_LANGUAGE, payload: lang};
			const request = actions.translationRequest(offer.id, lang);

			if (lang === defaultLanguage) return dispatch(actionChangeLang);

			dispatch(parentActions.toggleProgressBar());

			dispatch(request).then(action => {
				if (action.type === TRANSLATION_REQUEST_SUCCESS) {
					dispatch({
						...actionChangeLang,
						meta: action.payload.data
					});
				}
			});
		},

		translationRequest: (id, lang, method = 'get', data = {}) => dispatch => {
			const url = endpoints.OFFERS.LOCATION_RESTRICTIONS_TRANSLATIONS.replace('{id}', id).replace('{lang}', lang);
			return (http({
				url,
				method,
				actions: [TRANSLATION_REQUEST_SUCCESS, TRANSLATION_REQUEST_ERROR],
				data
			}))(dispatch);
		}
	};

	// reducers
	const reducers = {
		[CHANGE_LANGUAGE]: (state, action) => {
			const { payload, meta } = action;

			return {
				...state,
				showProgressBar: false,
				restrictionGeoForm: {
					...state.restrictionGeoForm,
					currentLanguage: payload,
					translateFields: meta ? meta : []
				}
			};
		},
		[FORM_SUBMIT_ERROR]: errorReducerFn,
		[TRANSLATION_REQUEST_ERROR]: errorReducerFn,
		[DELETE_REQUEST_ERROR]: errorReducerFn,
		[FETCH_LOCATION_LOOKUPS_ERROR]: errorReducerFn
	};

	// selectors
	const selectors = {
		getRestrictedGeoFormData: state => {
			const {
				locationRestrictions,
				restrictionGeoForm: { currentLanguage, translateFields },
				defaultLanguage
			} = state.programsEditor;

			const shouldTranslate = defaultLanguage !== currentLanguage;

			const fields = locationRestrictions && locationRestrictions.map(entity => {
				const cloneEntity = cloneDeep(entity);

				if (shouldTranslate) {
					const translate = find(translateFields, { 'location_restriction_id' : cloneEntity.id });
					cloneEntity.text = translate ? translate.text: '';
				}

				return {
					id: cloneEntity.id,
					location: [{value: cloneEntity.location_id, label: cloneEntity.location_name}],
					text: cloneEntity.text
				};
			});

			return {
				initialValues: {
					geo: fields
				},
				isTranslateTab: shouldTranslate
			};
		}
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};

};

const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return ({ ...state, error: true, showProgressBar: false });
	}
	return state;
};

export default createRestrictionGeoForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/programs/components/forms/restricted-geo/index.js